import React, { useContext, useMemo, useState } from 'react';
import {
	IFinancialRisk,
	IFinancialRiskMitigationAction,
	ListDefaults,
	ICorporateRiskMitigationAction,
	ICorporateRisk,
	IRiskAppIssue,
	IRiskAppIncident,
	IRiskAppIssueAction,
	IRiskAppIncidentAction
} from '../types';
import { EntityList } from './EntityList';
import { RiskForm } from './risk/RiskForm';
import { RiskMitigationActionForm } from './riskMitigationAction/RiskMitigationActionForm';
import { ErrorBoundary } from './ErrorBoundary';
import { IWithErrorHandlingProps, withErrorHandling } from './withErrorHandling';
import { IUseApiProps } from './useApi';
import { FinancialRiskForm } from './financialRisk/FinancialRiskForm';
import { DataContext } from './DataContext';
import { FinancialRiskMitigationActionForm } from './financialRisk/FinancialRiskMitigationActionForm';
import { OrbUserContext } from './OrbUserContext';
import { RiskView } from './risk/RiskView';
import { ProjectRiskMitigationActionView } from './riskMitigationAction/ProjectRiskMitigationActionView';
import { useHistory, useParams } from 'react-router-dom';
import { useLocation } from "react-router-dom";
import { IssueFormPage } from './risk/IssueFormPage';
import { renderDateRange, renderRiskIndicators } from './cr/ListRenderers';
import { IncidentFormPage } from './risk/IncidentFormPage';
import { IssueActionFormPage } from './risk/IssueActionFormPage';
import { IncidentActionFormPage } from './risk/IncidentActionFormPage';
import { RiskMitigationActionPageForm } from './riskMitigationAction/RiskMitigationActionPageForm';

export const impactTitles: Record<number, string> = {
	1: "Very Low",
	2: "Low",
	3: "Medium",
	4: "High",
	5: "Very High",
};

export const probabilityTitles: Record<number, string> = {
	1: "Very Unlikely (<10%)",
	2: "Unlikely (>10%-<35%)",
	3: "Possible (>35%-<65%)",
	4: "Likely (>65%-<90%)",
	5: "Very Likely (>90%)",
};

export interface IRiskReportingAdminProps extends IUseApiProps, IWithErrorHandlingProps {
	entity: string;
}

const RiskReportingAdmin = (props: IRiskReportingAdminProps): React.ReactElement => {
	const history = useHistory();
	const { entity } = props;
	const { dataServices, loadLookupData: { corporateRisks, financialRisks } } = useContext(DataContext);

	useMemo(() => corporateRisks(), [corporateRisks]);
	useMemo(() => financialRisks(), [financialRisks]);
	const { orbConfig } = useContext(OrbUserContext);
	const [riskMitigatingActionsLabelForSelectedRisk, setRiskMitigatingActionsLabelForSelectedRisk] = useState<string>("Risk mitigating actions");
	const [correctiveActionsLabelForSelectedIssue, setCorrectiveActionsLabelForSelectedIssue] = useState<string>("Corrective actions");
	const [correctiveActionsLabelForSelectedIncident, setCorrectiveActionsLabelForSelectedIncident] = useState<string>("Corrective actions");
	const { riskIdForMitigatingActions } = useParams<{ riskIdForMitigatingActions: string }>();
	const { issueIdForActions } = useParams<{ issueIdForActions: string }>();

	const { incidentIdForActions } = useParams<{ incidentIdForActions: string }>();
	const { probabilityFilter, impactLevelFilter } = useParams<{ probabilityFilter: string; impactLevelFilter: string }>();

	const location = useLocation<{ message?: string }>();
	const risksWithFilters_message = location.state?.message;

	//console.log("probabilityFilter:", probabilityFilter);
	//console.log("impactLevelFilter:", impactLevelFilter);
	//console.log('risksWithFilters_message', risksWithFilters_message);

	const lcc = ListDefaults.columnWidths;
	return (
		<ErrorBoundary>
			{entity === 'Risks' &&
				<EntityList<ICorporateRisk>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Risks', Singular: 'Risk' }}
					preFarItemsOnSelection={(r) => {
						setRiskMitigatingActionsLabelForSelectedRisk(`Risk mitigating actions (${r?.RiskMitigationActions?.length ?? 0})`);
						return ([
							{
								key: 'Updates',
								name: 'Updates',
								iconProps: { iconName: 'MiniExpand' },
								onClick: (ev, item) => {
									history.push(`/a3/sign-off-for-risk/${r?.ID}`);
								},
							},
							{
								key: 'RiskMitigatingActions',
								name: riskMitigatingActionsLabelForSelectedRisk,
								iconProps: { iconName: 'MiniExpand' },
								onClick: (ev, item) => {
									history.push(`/a3/risk-mitigating-actions-for-risk/${r?.ID}`);
								},
							},
						]);
					}}
					columns={[
						{ key: '1', name: 'Risk ID', fieldName: 'RiskCode', minWidth: 70, maxWidth: 70, isResizable: true },
						{ key: '2', name: 'Risk name', fieldName: 'Name', minWidth: 100, maxWidth: 150, isMultiline: true, isResizable: true },
						{ key: '3', name: 'Impact', minWidth: 50, maxWidth: 50, isResizable: true, onRender: item => renderRiskIndicators('Impact', false, item.ShowUnmitigatedTick, item.UnmitigatedRiskProbabilityID, item.UnmitigatedRiskImpactLevelID, item.TargetRiskProbabilityID, item.TargetRiskImpactLevelID) },
						{ key: '4', name: 'Probability', minWidth: 50, maxWidth: 70, isResizable: true, onRender: item => renderRiskIndicators('Probability', false, item.ShowUnmitigatedTick, item.UnmitigatedRiskProbabilityID, item.UnmitigatedRiskImpactLevelID, item.TargetRiskProbabilityID, item.TargetRiskImpactLevelID) },
						{ key: '5', name: 'Register', fieldName: 'Register', minWidth: 100, maxWidth: 170, isMultiline: true, isResizable: true },
						{ key: '6', name: 'Managed By', fieldName: 'ManagedBy', minWidth: 100, maxWidth: 150, isMultiline: true, isResizable: true },
						{ key: '7', name: 'Staff', fieldName: 'RiskStaff', minWidth: 100, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '8', name: 'Status', fieldName: 'Status', minWidth: 50, maxWidth: 50, isResizable: true }
					]}
					loadListItems={showClosedEntities => dataServices.corporateRiskService.readAllForList_RiskRegister(showClosedEntities)}
					mapEntitiesToListItems={risks => risks.map(r => {
						let owner = '';
						if (r.RiskRegisterID !== null) {
							switch (r.RiskRegisterID) {
								case 0:
									owner = `${orbConfig.OrgTypeLabel}`;
									break;
								case 1:
									owner = `${orbConfig.OrgL1Label} ${r.OwnerL1?.Title ?? ''}`;
									break;
								case 2:
									owner = `${orbConfig.OrgL2Label} ${r.OwnerL2?.Title ?? ''}`;
									break;
								case 3:
									owner = `${orbConfig.OrgL3Label} ${r.OwnerL3?.Title ?? ''}`;
									break;
								default:
									owner = '';
									break;
							}
						}

						let managedBy = '';
						if (r.ManagedByTypeID !== null) {
							switch (r.ManagedByTypeID) {
								case 2:
									managedBy = `${orbConfig.OrgL2Label} ${r.ManagedByL2?.Title ?? ''}`;
									break;
								case 3:
									managedBy = `${orbConfig.OrgL3Label} ${r.ManagedByL3?.Title ?? ''}`;
									break;
								default:
									managedBy = '';
									break;
							}
						}

						return {
							key: r.ID,
							Register: owner,
							UnmitigatedRiskProbabilityID: r.UnmitigatedRiskProbabilityID,
							UnmitigatedRiskImpactLevelID: r.UnmitigatedRiskImpactLevelID,
							TargetRiskProbabilityID: r.TargetRiskProbabilityID,
							TargetRiskImpactLevelID: r.TargetRiskImpactLevelID,
							ManagedBy: managedBy,
							RiskCode: r.RiskCode,
							Name: r.Title,
							Group: r.Directorate?.OrgLevel1?.Title,
							Directorate: r.Directorate?.Title,
							RiskStaff: [
								r.RiskOwnerUser ? `Owner - ${r.RiskOwnerUser.Title}` : null,
								r.ReportApproverUser ? `ARA - ${r.ReportApproverUser.Title}` : null,
								r.Contributors && r.Contributors.some(c => c.ContributorUser != null) ? `C - ${r.Contributors.map(pc => pc.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							Status: r.EntityStatus?.Title
						};
					})}
					formTypePage={true}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<RiskForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					viewForm={(showForm, entityId, onClosed) =>
						<RiskView
							{...props}
							showForm={showForm}
							entityId={entityId}
							entityName='Risk'
							loadEntity={riskId => dataServices.corporateRiskService.readForView(riskId)}
							onClosed={onClosed}
						/>
					}
					// childEntityForm={(showForm, parentEntityId, onSaved, onCancelled) =>
					// 	<RiskMitigationActionForm
					// 		{...props}
					// 		maxDependentRisks={1}
					// 		showForm={showForm}
					// 		onSaved={onSaved}
					// 		onCancelled={onCancelled}
					// 		defaultValues={[{ field: 'RiskID', value: parentEntityId }]}
					// 	/>
					// }
					onCheckDelete={id => dataServices.corporateRiskService.entityChildren(id)}
					onDelete={id => dataServices.corporateRiskService.delete(id)}
					onChange={() => corporateRisks(true)}
				/>
			}
			{entity === 'RisksWithFilters' &&
				<EntityList<ICorporateRisk>
					{...props}
					disableAdd={true}
					enableShowHideClosedEntities={true}
					entityName={
						{
							//Plural: `Risks with ${risksWithFilters_message}`,
							Plural: `Risks with Impact ${impactTitles[impactLevelFilter]} - ${probabilityTitles[probabilityFilter]}`,
							Singular: 'Risk'
						}
					}
					veryFirstItems={(r) => {
						return ([
							{
								key: 'Insights',
								name: 'Back to Insights',
								iconProps: { iconName: 'Back' },
								onClick(ev, item) {
									history.push(`/a3/insights`);
								},
							},
						]);
					}}

					//TODO: following, have to set 2 filter values probabilityFilter, impactLevelFilter in parent component and access in this component via props
					//for that see example in PerformanceReportingAdmin:
					//	lastPortfolioIdForChilds?: number | null; setLastPortfolioIdForChilds?: React.Dispatch<React.SetStateAction<number | null>>;


					// preFarItemsOnSelection={(r) => {
					// 	setRiskMitigatingActionsLabelForSelectedRisk(`Risk mitigating actions (${r?.RiskMitigationActions?.length ?? 0})`);
					// 	return ([
					// 		{
					// 			key: 'Updates',
					// 			name: 'Updates',
					// 			iconProps: { iconName: 'MiniExpand' },
					// 			onClick: (ev, item) => {
					// 				history.push(`/a3/updates-for-risk/${r?.ID}`);
					// 			},
					// 		},
					// 		{
					// 			key: 'RiskMitigatingActions',
					// 			name: riskMitigatingActionsLabelForSelectedRisk,
					// 			iconProps: { iconName: 'MiniExpand' },
					// 			onClick: (ev, item) => {
					// 				history.push(`/a3/risk-mitigating-actions-for-risk/${r?.ID}`);
					// 			},
					// 		},
					// 	]);
					// }}
					columns={[
						{ key: '1', name: 'Risk ID', fieldName: 'RiskCode', minWidth: 70, maxWidth: 70, isResizable: true },
						{ key: '2', name: 'Risk name', fieldName: 'Name', minWidth: 100, maxWidth: 150, isMultiline: true, isResizable: true },
						{ key: '3', name: 'Impact', minWidth: 50, maxWidth: 50, isResizable: true, onRender: item => renderRiskIndicators('Impact', item.ShowTargetTick, item.ShowUnmitigatedTick, item.UnmitigatedRiskProbabilityID, item.UnmitigatedRiskImpactLevelID, item.TargetRiskProbabilityID, item.TargetRiskImpactLevelID) },
						{ key: '4', name: 'Probability', minWidth: 50, maxWidth: 70, isResizable: true, onRender: item => renderRiskIndicators('Probability', item.ShowTargetTick, item.ShowUnmitigatedTick, item.UnmitigatedRiskProbabilityID, item.UnmitigatedRiskImpactLevelID, item.TargetRiskProbabilityID, item.TargetRiskImpactLevelID) },
						{ key: '5', name: 'Register', fieldName: 'Register', minWidth: 100, maxWidth: 170, isMultiline: true, isResizable: true },
						{ key: '6', name: 'Managed By', fieldName: 'ManagedBy', minWidth: 100, maxWidth: 150, isMultiline: true, isResizable: true },
						{ key: '7', name: 'Staff', fieldName: 'RiskStaff', minWidth: 100, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '8', name: 'Status', fieldName: 'Status', minWidth: 50, maxWidth: 50, isResizable: true }
					]}
					loadListItems={showClosedEntities => dataServices.corporateRiskService.readAllForListWithFilters_RiskRegister(Number(probabilityFilter), Number(impactLevelFilter), showClosedEntities)}
					mapEntitiesToListItems={risks => risks.map(r => {
						let owner = '';
						if (r.RiskRegisterID !== null) {
							switch (r.RiskRegisterID) {
								case 0:
									owner = `${orbConfig.OrgTypeLabel}`;
									break;
								case 1:
									owner = `${orbConfig.OrgL1Label} ${r.OwnerL1?.Title ?? ''}`;
									break;
								case 2:
									owner = `${orbConfig.OrgL2Label} ${r.OwnerL2?.Title ?? ''}`;
									break;
								case 3:
									owner = `${orbConfig.OrgL3Label} ${r.OwnerL3?.Title ?? ''}`;
									break;
								default:
									owner = '';
									break;
							}
						}

						let managedBy = '';
						if (r.ManagedByTypeID !== null) {
							switch (r.ManagedByTypeID) {
								case 2:
									managedBy = `${orbConfig.OrgL2Label} ${r.ManagedByL2?.Title ?? ''}`;
									break;
								case 3:
									managedBy = `${orbConfig.OrgL3Label} ${r.ManagedByL3?.Title ?? ''}`;
									break;
								default:
									managedBy = '';
									break;
							}
						}

						//target tick
						let showTargetTick: boolean = false;
						if (r.TargetRiskProbabilityID === Number(probabilityFilter) && r.TargetRiskImpactLevelID === Number(impactLevelFilter)) {
							showTargetTick = true;
						}

						//unmitigatedTick tick
						let showUnmitigatedTick: boolean = false;
						if (r.UnmitigatedRiskProbabilityID === Number(probabilityFilter) && r.UnmitigatedRiskImpactLevelID === Number(impactLevelFilter)) {
							showUnmitigatedTick = true;
						}

						return {
							key: r.ID,
							UnmitigatedRiskProbabilityID: r.UnmitigatedRiskProbabilityID,
							UnmitigatedRiskImpactLevelID: r.UnmitigatedRiskImpactLevelID,
							TargetRiskProbabilityID: r.TargetRiskProbabilityID,
							TargetRiskImpactLevelID: r.TargetRiskImpactLevelID,
							ShowTargetTick: showTargetTick,
							ShowUnmitigatedTick: showUnmitigatedTick,
							Register: owner,
							ManagedBy: managedBy,
							RiskCode: r.RiskCode,
							Name: r.Title,
							Group: r.Directorate?.OrgLevel1?.Title,
							Directorate: r.Directorate?.Title,
							RiskStaff: [
								r.RiskOwnerUser ? `Owner - ${r.RiskOwnerUser.Title}` : null,
								r.ReportApproverUser ? `ARA - ${r.ReportApproverUser.Title}` : null,
								r.Contributors && r.Contributors.some(c => c.ContributorUser != null) ? `C - ${r.Contributors.map(pc => pc.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							Status: r.EntityStatus?.Title
						};
					})}
					formTypePage={true}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<RiskForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					viewForm={(showForm, entityId, onClosed) =>
						<RiskView
							{...props}
							showForm={showForm}
							entityId={entityId}
							entityName='Risk'
							loadEntity={riskId => dataServices.corporateRiskService.readForView(riskId)}
							onClosed={onClosed}
						/>
					}
					// childEntityForm={(showForm, parentEntityId, onSaved, onCancelled) =>
					// 	<RiskMitigationActionForm
					// 		{...props}
					// 		maxDependentRisks={1}
					// 		showForm={showForm}
					// 		onSaved={onSaved}
					// 		onCancelled={onCancelled}
					// 		defaultValues={[{ field: 'RiskID', value: parentEntityId }]}
					// 	/>
					// }
					onCheckDelete={id => dataServices.corporateRiskService.entityChildren(id)}
					onDelete={id => dataServices.corporateRiskService.delete(id)}
					onChange={() => corporateRisks(true)}
				/>
			}
			{entity === 'Issues' &&
				<EntityList<IRiskAppIssue>
					{...props}
					enableShowHideClosedEntities={true}
					entity={issueIdForActions !== undefined ? "Corrective Actions for Issue" : "Corrective Actions"} //just to reload
					entityName={{ Plural: 'Issues', Singular: 'Issue' }}
					preFarItemsOnSelection={(issue) => {
						setCorrectiveActionsLabelForSelectedIssue(`Corrective actions (${issue?.RiskAppIssueActions?.length ?? 0})`);
						return ([
							{
								key: 'CorrectiveActions',
								name: correctiveActionsLabelForSelectedIssue,
								iconProps: { iconName: 'MiniExpand' },
								onClick: (ev, item) => {
									history.push(`/a3/issues-actions-for-issue/${issue?.ID}`);
								},
							},
						]);
					}}
					columns={[
						//{ key: '1', name: 'Risk ID', fieldName: 'RiskCode', minWidth: 70, maxWidth: 70, isResizable: true },
						{ key: '2', name: 'Issue name', fieldName: 'Name', minWidth: 100, maxWidth: 150, isResizable: true, isMultiline: true },
						{ key: '3', name: 'Source', fieldName: 'Source', minWidth: 50, maxWidth: 90, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Priority', fieldName: 'Priority', minWidth: 50, maxWidth: 70, isResizable: true, isCollapsible: true },
						{ key: '5', name: 'Status/Stage', fieldName: 'CombinedStatusStage', minWidth: 70, maxWidth: 100, isResizable: true, isCollapsible: true },
						{ key: '6', name: 'Managed By', fieldName: 'ManagedBy', minWidth: 100, maxWidth: 250, isCollapsible: true, isResizable: true, isMultiline: true },
						{ key: '7', name: 'Staff', fieldName: 'Staff', minWidth: 150, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '8', name: 'Dates', minWidth: 100, isResizable: true, isCollapsible: true, onRender: item => renderDateRange(item.DateIdentified, item.TargetResolutionDate) },
					]}
					loadListItems={showClosedEntities => dataServices.riskAppIssueService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={issues => issues.map((i, index) => {
						const combinedStatusStage = `${i.EntityStatus?.Title ?? ''}${i.EntityStatus?.Title && i.Stage?.Title ? '-' : ''}${i.Stage?.Title ?? ''}`;

						let owner = '';
						if (i.OwnerTypeID !== null) {
							switch (i.OwnerTypeID) {
								case 0:
									owner = `${orbConfig.OrgTypeLabel}`;
									break;
								case 1:
									owner = `${orbConfig.OrgL1Label} ${i.OwnerL1.Title}`;
									break;
								case 2:
									owner = `${orbConfig.OrgL2Label} ${i.OwnerL2.Title}`;
									break;
								case 3:
									owner = `${orbConfig.OrgL3Label} ${i.OwnerL3.Title}`;
									break;
								default:
									owner = '';
									break;
							}

						}

						let managedBy = '';
						if (i.ManagedByTypeID !== null) {
							switch (i.ManagedByTypeID) {
								case 2:
									managedBy = `${orbConfig.OrgL2Label} ${i.ManagedByL2.Title}, for ${owner}`;
									break;
								case 3:
									managedBy = `${orbConfig.OrgL3Label} ${i.ManagedByL3.Title}, for ${owner}`;
									break;
								default:
									managedBy = '';
									break;
							}
						}

						return {
							key: i.ID,
							Name: i.Title,
							Source: i.Source?.Title,
							Priority: i.Priority?.Title,
							CombinedStatusStage: combinedStatusStage,
							ManagedBy: managedBy,
							Staff: [
								i.IssueManager ? `IM - ${i.IssueManager.Title}` : null,
								i.Contributors && i.Contributors.some(c => c.ContributorUser != null) ? `C - ${i.Contributors.map(c => c.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							DateIdentified: i.DateIdentified,
							DateIdentifiedValue: i.DateIdentified?.valueOf(),
							TargetResolutionDate: i.TargetResolutionDate,
							TargetResolutionDateValue: i.TargetResolutionDate?.valueOf(),
						};
					})}
					formTypePage={true}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<IssueFormPage
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
							workflowID={0}
							newTaskStageID={0}
							{...props}
							workflowTitle='test'
						/>
					}
					onCheckDelete={id => dataServices.riskAppIssueService.entityChildren(id)}
					onDelete={id => dataServices.riskAppIssueService.delete(id)}
				/>
			}
			{entity === 'IssuesActions' &&
				<EntityList<IRiskAppIssueAction>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Corrective Actions', Singular: 'Corrective Action' }}
					veryFirstItems={(p) => {
						if (issueIdForActions !== undefined)
							return ([
								{
									key: 'Issues',
									name: 'Back to Issues',
									iconProps: { iconName: 'Back' },
									onClick(ev, item) {
										history.push(`/a3/issues`);
									},
								},
							]);
						return null;
					}}
					showEntityDescription1={issueIdForActions !== undefined ? true : false}
					loadEntityDescription1={() => {
						return dataServices.riskAppIssueService.readTitleForCorrectiveActions('Corrective actions', 'Corrective action', Number(issueIdForActions));
					}}
					columns={[
						{ key: '1', name: 'Corrective Action', fieldName: 'Name', minWidth: 100, maxWidth: 150, isResizable: true, isMultiline: true },
						...(issueIdForActions === undefined
							? [{
								key: '2',
								name: 'Issue',
								fieldName: 'Issue',
								minWidth: 90,
								maxWidth: 130,
								isResizable: true,
								isCollapsible: true
							}]
							: []),
						{ key: '3', name: 'Staff', fieldName: 'Staff', minWidth: 150, maxWidth: 250, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '4', name: 'Dates', minWidth: 100, maxWidth: 120, isResizable: true, isCollapsible: true, onRender: item => renderDateRange(item.ActualStartDate, item.TargetCompletionDate) },
						{ key: '5', name: 'Status/Stage', fieldName: 'CombinedStatusStage', minWidth: 70, isResizable: true, isCollapsible: true },

					]}
					loadListItems={showClosedEntities => dataServices.riskAppIssueActionService.readAllForList(showClosedEntities, Number(issueIdForActions))}
					mapEntitiesToListItems={actions => actions.map((a, index) => {
						const combinedStatusStage = `${a.EntityStatus?.Title ?? ''}${a.EntityStatus?.Title && a.Stage?.Title ? '-' : ''}${a.Stage?.Title ?? ''}`;
						return {
							key: a.ID,
							Name: a.Title,
							Issue: a.Issue?.Title,
							Staff: [
								a.ActionOwner ? `AO - ${a.ActionOwner.Title}` : null,
								a.Contributors && a.Contributors.some(c => c.ContributorUser != null) ? `C - ${a.Contributors.map(c => c.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							CombinedStatusStage: combinedStatusStage,
							ActualStartDate: a.ActualStartDate,
							ActualStartDateValue: a.ActualStartDate?.valueOf(),
							TargetCompletionDate: a.TargetCompletionDate,
							TargetCompletionDateValue: a.TargetCompletionDate?.valueOf(),
						};
					})}
					formTypePage={true}
					entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
						<IssueActionFormPage
							showForm={showForm}
							entityId={entityId}
							showEntityDescription1={issueIdForActions !== undefined ? true : false}
							entityDescription1={entityDescription1}
							defaultValues={issueIdForActions !== undefined ? [{ field: 'IssueID', value: Number(issueIdForActions) }] : []}
							onSaved={onSaved}
							onCancelled={onCancelled}
							workflowID={0}
							newTaskStageID={0}
							{...props}
							workflowTitle='test'
						/>
					}
					onCheckDelete={id => dataServices.riskAppIssueActionService.entityChildren(id)}
					onDelete={id => dataServices.riskAppIssueActionService.delete(id)}
				/>
			}
			{entity === 'Incidents' &&
				<EntityList<IRiskAppIncident>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Incidents', Singular: 'Incident' }}
					entity={incidentIdForActions !== undefined ? "Corrective Actions for Incidents" : "Corrective Actions"} //just to reload
					preFarItemsOnSelection={(incident) => {
						setCorrectiveActionsLabelForSelectedIncident(`Corrective actions (${incident?.RiskAppIncidentActions?.length ?? 0})`);
						return ([
							{
								key: 'CorrectiveActions',
								name: correctiveActionsLabelForSelectedIncident,
								iconProps: { iconName: 'MiniExpand' },
								onClick: (ev, item) => {
									history.push(`/a3/incidents-actions-for-incident/${incident?.ID}`);
								},
							},
						]);
					}}
					columns={[
						//{ key: '1', name: 'Risk ID', fieldName: 'RiskCode', minWidth: 70, maxWidth: 70, isResizable: true },
						{ key: '2', name: 'Incident name', fieldName: 'Name', minWidth: 100, maxWidth: 150, isResizable: true, isMultiline: true },
						{ key: '3', name: 'System', fieldName: 'SystemLocation', minWidth: 50, maxWidth: 90, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Severity', fieldName: 'Severity', minWidth: 50, maxWidth: 70, isResizable: true, isCollapsible: true },
						{ key: '5', name: 'Status/Stage', fieldName: 'CombinedStatusStage', minWidth: 70, maxWidth: 100, isResizable: true, isCollapsible: true },
						{ key: '6', name: 'Managed By', fieldName: 'ManagedBy', minWidth: 100, maxWidth: 250, isCollapsible: true, isResizable: true, isMultiline: true },
						{ key: '7', name: 'Staff', fieldName: 'Staff', minWidth: 150, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '8', name: 'Dates', minWidth: 100, isResizable: true, isCollapsible: true, onRender: item => renderDateRange(item.DateReported, item.TargetResolutionDate) },
					]}
					loadListItems={showClosedEntities => dataServices.riskAppIncidentService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={incidents => incidents.map((inc, index) => {
						const locationTitle = inc.Location?.Title?.trim();
						const systemTitles = inc.RiskAppIncidentSystems
							?.map(rs => rs.System?.Title?.trim())
							.filter(Boolean)
							.join(', ');

						let systemLocation = '';

						if (locationTitle && systemTitles) {
							systemLocation = `${locationTitle} - ${systemTitles}`;
						} else if (locationTitle) {
							systemLocation = locationTitle;
						} else if (systemTitles) {
							systemLocation = systemTitles;
						}

						const combinedStatusStage = `${inc.EntityStatus?.Title ?? ''}${inc.EntityStatus?.Title && inc.Stage?.Title ? '-' : ''}${inc.Stage?.Title ?? ''}`;

						let owner = '';
						if (inc.OwnerTypeID !== null) {
							switch (inc.OwnerTypeID) {
								case 0:
									owner = `${orbConfig.OrgTypeLabel}`;
									break;
								case 1:
									owner = `${orbConfig.OrgL1Label} ${inc.OwnerL1.Title}`;
									break;
								case 2:
									owner = `${orbConfig.OrgL2Label} ${inc.OwnerL2.Title}`;
									break;
								case 3:
									owner = `${orbConfig.OrgL3Label} ${inc.OwnerL3.Title}`;
									break;
								default:
									owner = '';
									break;
							}

						}

						let managedBy = '';
						if (inc.ManagedByTypeID !== null) {
							switch (inc.ManagedByTypeID) {
								case 2:
									managedBy = `${orbConfig.OrgL2Label} ${inc.ManagedByL2.Title}, for ${owner}`;
									break;
								case 3:
									managedBy = `${orbConfig.OrgL3Label} ${inc.ManagedByL3.Title}, for ${owner}`;
									break;
								default:
									managedBy = '';
									break;
							}
						}

						return {
							key: inc.ID,
							Name: inc.Title,
							SystemLocation: systemLocation,
							Severity: inc.Severity?.Title,
							CombinedStatusStage: combinedStatusStage,
							ManagedBy: managedBy,
							Staff: [
								inc.IncidentManager ? `IM - ${inc.IncidentManager.Title}` : null,
								inc.Contributors && inc.Contributors.some(c => c.ContributorUser != null) ? `C - ${inc.Contributors.map(c => c.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							DateReported: inc.DateReported,
							DateReportedValue: inc.DateReported?.valueOf(),
							TargetResolutionDate: inc.TargetResolutionDate,
							TargetResolutionDateValue: inc.TargetResolutionDate?.valueOf(),
						};
					})}
					formTypePage={true}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<IncidentFormPage
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
							workflowID={0}
							newTaskStageID={0}
							{...props}
							workflowTitle='test'
						/>
					}
					onCheckDelete={id => dataServices.riskAppIncidentService.entityChildren(id)}
					onDelete={id => dataServices.riskAppIncidentService.delete(id)}
					onChange={() => corporateRisks(true)}
				/>
			}
			{entity === 'IncidentsActions' &&
				<EntityList<IRiskAppIncidentAction>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Corrective Actions', Singular: 'Corrective Action' }}
					veryFirstItems={(p) => {
						if (incidentIdForActions !== undefined)
							return ([
								{
									key: 'Incidents',
									name: 'Back to Incidents',
									iconProps: { iconName: 'Back' },
									onClick(ev, item) {
										history.push(`/a3/incidents`);
									},
								},
							]);
						return null;
					}}
					showEntityDescription1={incidentIdForActions !== undefined ? true : false}
					loadEntityDescription1={() => {
						return dataServices.riskAppIncidentService.readTitleForCorrectiveActions('Corrective actions', 'Corrective action', Number(incidentIdForActions));
					}}
					columns={[
						{ key: '1', name: 'Corrective Action', fieldName: 'Name', minWidth: 100, maxWidth: 150, isResizable: true, isMultiline: true },
						...(incidentIdForActions === undefined
							? [{ key: '2', name: 'Incident', fieldName: 'Incident', minWidth: 90, maxWidth: 130, isResizable: true, isCollapsible: true },]
							: []),
						{ key: '3', name: 'Staff', fieldName: 'Staff', minWidth: 150, maxWidth: 250, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '4', name: 'Dates', minWidth: 100, maxWidth: 120, isResizable: true, isCollapsible: true, onRender: item => renderDateRange(item.ActualStartDate, item.TargetCompletionDate) },
						{ key: '5', name: 'Status/Stage', fieldName: 'CombinedStatusStage', minWidth: 70, isResizable: true, isCollapsible: true },

					]}
					loadListItems={showClosedEntities => dataServices.riskAppIncidentActionService.readAllForList(showClosedEntities, Number(incidentIdForActions))}
					mapEntitiesToListItems={actions => actions.map((a, index) => {
						const combinedStatusStage = `${a.EntityStatus?.Title ?? ''}${a.EntityStatus?.Title && a.Stage?.Title ? '-' : ''}${a.Stage?.Title ?? ''}`;
						return {
							key: a.ID,
							Name: a.Title,
							Incident: a.Incident?.Title,
							Staff: [
								a.ActionOwner ? `AO - ${a.ActionOwner.Title}` : null,
								a.Contributors && a.Contributors.some(c => c.ContributorUser != null) ? `C - ${a.Contributors.map(c => c.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							CombinedStatusStage: combinedStatusStage,
							ActualStartDate: a.ActualStartDate,
							ActualStartDateValue: a.ActualStartDate?.valueOf(),
							TargetCompletionDate: a.TargetCompletionDate,
							TargetCompletionDateValue: a.TargetCompletionDate?.valueOf(),
						};
					})}
					formTypePage={true}
					entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
						<IncidentActionFormPage
							showForm={showForm}
							entityId={entityId}
							showEntityDescription1={incidentIdForActions !== undefined ? true : false}
							entityDescription1={entityDescription1}
							defaultValues={incidentIdForActions !== undefined ? [{ field: 'IncidentID', value: Number(incidentIdForActions) }] : []}
							onSaved={onSaved}
							onCancelled={onCancelled}
							workflowID={0}
							newTaskStageID={0}
							{...props}
							workflowTitle='test'
						/>
					}
					onCheckDelete={id => dataServices.riskAppIncidentActionService.entityChildren(id)}
					onDelete={id => dataServices.riskAppIncidentActionService.delete(id)}
				/>
			}
			{entity === 'RiskMitigationActions' &&
				<EntityList<ICorporateRiskMitigationAction>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Risk mitigating actions', Singular: 'Risk mitigating action' }}
					entity={riskIdForMitigatingActions !== undefined ? "Actions for Risk" : "Risk mitigating actions"} //just to reload
					columns={[
						{ key: '0', name: 'Risk ID', fieldName: 'RiskCode', minWidth: 50, maxWidth: 70, isResizable: true },
						//{ key: '1', name: 'ID', fieldName: 'Code', minWidth: 20, maxWidth: 20, isResizable: true },
						{ key: '2', name: 'Risk mitigating action name', fieldName: 'Name', minWidth: 300, isResizable: true },
						{ key: '3', name: 'Register', fieldName: 'Register', minWidth: 200, isResizable: true },
						{ key: '4', name: 'Risk', fieldName: 'Risk', minWidth: 200, isResizable: true },
						//{ key: '4', name: 'Owner', fieldName: 'Owner', minWidth: lcc.user, isResizable: true },
						//{ key: '5', name: 'Contributors', fieldName: 'Contributors', minWidth: lcc.user, isResizable: true },
						{ key: '5', name: 'Staff', fieldName: 'ActionStaff', minWidth: 250, maxWidth: 350, isResizable: true, isMultiline: true, isCollapsible: true },
						{ key: '6', name: 'Status', fieldName: 'Status', minWidth: 50, isResizable: true }
					]}
					loadListItems={showClosedEntities => dataServices.corporateRiskMitigationActionService.readAllForList_RiskRegister(showClosedEntities, Number(riskIdForMitigatingActions))}
					mapEntitiesToListItems={riskMitigationActions => riskMitigationActions.map(a => {

						let owner = '';
						if (a.Risk?.RiskRegisterID !== null) {
							switch (a.Risk?.RiskRegisterID) {
								case 0:
									owner = `Dept`;
									break;
								case 1:
									owner = `${orbConfig.OrgL1Label} ${a.Risk?.OwnerL1?.Title ?? ''}`;
									break;
								case 2:
									owner = `${orbConfig.OrgL2Label} ${a.Risk?.OwnerL2?.Title ?? ''}`;
									break;
								case 3:
									owner = `${orbConfig.OrgL3Label} ${a.Risk?.OwnerL3?.Title ?? ''}`;
									break;
								default:
									owner = '';
									break;
							}
						}

						return {
							key: a.ID,
							RiskCode: a.Risk?.RiskCode,
							Code: a.RiskMitigationActionCode,
							Register: owner,
							Risk: a.Risk?.Title,
							Name: a.Title,
							//Owner: a.OwnerUser?.Title,
							//Contributors: a.Contributors?.map(c => c.ContributorUser?.Title).join(', '),
							ActionStaff: [
								a.OwnerUser ? `Owner - ${a.OwnerUser.Title}` : null,
								a.Contributors && a.Contributors.some(c => c.ContributorUser != null) ? `C - ${a.Contributors.map(c => c.ContributorUser?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							Status: a.EntityStatus?.Title
						};
					})}
					veryFirstItems={(p) => {
						if (riskIdForMitigatingActions !== undefined) {
							if (impactLevelFilter !== undefined && probabilityFilter !== undefined) {
								return ([
									{
										key: 'Risks',
										name: 'Back to Risks',
										iconProps: { iconName: 'Back' },
										onClick(ev, item) {
											history.push(`/a3/risks-with-filters/${probabilityFilter}/${impactLevelFilter}`);
										},
									},
								]);
							}

							return ([
								{
									key: 'Risks',
									name: 'Back to Risks',
									iconProps: { iconName: 'Back' },
									onClick(ev, item) {
										history.push(`/a3/risks`);
									},
								},
							]);
						}
						return null;
					}}
					showEntityDescription1={riskIdForMitigatingActions !== undefined ? true : false}
					loadEntityDescription1={() => {
						return dataServices.corporateRiskService.readTitlesForRiskProjectDirectorateAndGroup('Risks mitigating actions', 'Risks mitigating action', Number(riskIdForMitigatingActions), null, true);
					}}
					formTypePage={true}
					entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
						<RiskMitigationActionPageForm
							{...props}
							showEntityDescription1={riskIdForMitigatingActions !== undefined ? true : false}
							entityDescription1={entityDescription1}
							defaultValues={riskIdForMitigatingActions !== undefined ? [{ field: 'RiskID', value: Number(riskIdForMitigatingActions) }] : []}
							maxDependentRisks={1}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					viewForm={(showForm, entityId, onClosed) =>
						<ProjectRiskMitigationActionView
							{...props}
							showForm={showForm}
							entityId={entityId}
							entityName='Risk mitigating action'
							loadEntity={actionId => dataServices.corporateRiskMitigationActionService.readForView(actionId)}
							onClosed={onClosed}
						/>
					}
					onCheckDelete={id => dataServices.corporateRiskMitigationActionService.entityChildren(id)}
					onDelete={id => dataServices.corporateRiskMitigationActionService.delete(id)}
				/>
			}
			{entity === 'FinancialRisks' &&
				<EntityList<IFinancialRisk>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Financial risks', Singular: 'Financial risk' }}
					addChild={{ Name: 'Risk mitigating action' }}
					columns={[
						{ key: '1', name: 'Risk ID', fieldName: 'RiskCode', minWidth: 70, maxWidth: 70, isResizable: true },
						{ key: '2', name: 'Risk name', fieldName: 'Name', minWidth: 250, isResizable: true },
						{ key: '4', name: 'Group', fieldName: 'Group', minWidth: 100, isResizable: true },
						{ key: '5', name: 'Directorate', fieldName: 'Directorate', minWidth: 100, isResizable: true },
						{ key: '6', name: 'Owner', fieldName: 'Owner', minWidth: lcc.user, isResizable: true },
						{ key: '7', name: 'Contributors', fieldName: 'Contributors', minWidth: lcc.user, isResizable: true },
						{ key: '8', name: 'No. of actions', fieldName: 'NumberOfActions', minWidth: 50, isResizable: true },
						{ key: '9', name: 'Status', fieldName: 'Status', minWidth: 50, isResizable: true }
					]}
					loadListItems={showClosedEntities => dataServices.financialRiskService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={risks => risks.map(r => (
						{
							key: r.ID,
							RiskCode: r.RiskCode,
							Name: r.Title,
							Group: r.OwnedByMultipleGroups ? `Central` : r.OrgLevel1?.Title,
							Directorate: r.OwnedByMultipleGroups ? `Central` : r.OwnedByDgOffice ? `DG Office` : r.Directorate?.Title,
							Owner: r.RiskOwnerUser?.Title,
							Contributors: r.Contributors?.map(c => c.ContributorUser?.Title).join(', '),
							NumberOfActions: r.FinancialRiskMitigationActions?.length,
							Status: r.EntityStatus?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<FinancialRiskForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					childEntityForm={(showForm, parentEntityId, onSaved, onCancelled) =>
						<FinancialRiskMitigationActionForm
							{...props}
							showForm={showForm}
							onSaved={onSaved}
							onCancelled={onCancelled}
							defaultValues={[{ field: 'RiskID', value: parentEntityId }]}
						/>
					}
					onCheckDelete={id => dataServices.financialRiskService.entityChildren(id)}
					onDelete={id => dataServices.financialRiskService.delete(id)}
					onChange={() => financialRisks(true)}
				/>
			}
			{entity === 'FinancialRiskMitigationActions' &&
				<EntityList<IFinancialRiskMitigationAction>
					{...props}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Financial risk mitigating actions', Singular: 'Financial risk mitigating action' }}
					columns={[
						{ key: '0', name: 'Risk ID', fieldName: 'RiskCode', minWidth: 50, maxWidth: 70, isResizable: true },
						{ key: '1', name: 'ID', fieldName: 'Code', minWidth: 20, maxWidth: 20, isResizable: true },
						{ key: '2', name: 'Risk mitigating action name', fieldName: 'Name', minWidth: 300, isResizable: true },
						{ key: '3', name: 'Risk', fieldName: 'Risk', minWidth: 200, isResizable: true },
						{ key: '4', name: 'Owner', fieldName: 'Owner', minWidth: lcc.user, isResizable: true },
						{ key: '5', name: 'Contributors', fieldName: 'Contributors', minWidth: lcc.user, isResizable: true },
						{ key: '6', name: 'Status', fieldName: 'Status', minWidth: 50, isResizable: true }
					]}
					loadListItems={showClosedEntities => dataServices.financialRiskMitigationActionService.readAllForList(showClosedEntities)}
					mapEntitiesToListItems={riskMitigationActions => riskMitigationActions.map(a => (
						{
							key: a.ID,
							RiskCode: a.FinancialRisk?.RiskCode,
							Code: a.RiskMitigationActionCode,
							Risk: a.FinancialRisk?.Title,
							Name: a.Title,
							Owner: a.OwnerUser?.Title,
							Contributors: a.Contributors?.map(c => c.ContributorUser?.Title).join(', '),
							Status: a.EntityStatus?.Title
						}
					))}
					entityForm={(showForm, entityId, onSaved, onCancelled) =>
						<FinancialRiskMitigationActionForm
							{...props}
							showForm={showForm}
							entityId={entityId}
							onSaved={onSaved}
							onCancelled={onCancelled}
						/>
					}
					onCheckDelete={id => dataServices.financialRiskMitigationActionService.entityChildren(id)}
					onDelete={id => dataServices.financialRiskMitigationActionService.delete(id)}
				/>
			}
		</ErrorBoundary>
	);
};

export default withErrorHandling(RiskReportingAdmin);
